import React, { useState } from 'react';
import { UserProfile, Tenant } from '../../types';
import { Key, Shield, Search, ChevronDown } from 'lucide-react';

const RegionDropdown: React.FC<{
  user: UserProfile,
  tenants: Tenant[],
  toggle: (user: UserProfile, tenantId: string) => void,
  updateUser: (uid: string, updates: Partial<UserProfile>) => void
}> = ({ user, tenants, toggle, updateUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const authorizedRegions = user.extendedRegions || [user.tenantId];
  const allTenantIds = tenants.map(t => t.id);
  const isAllSelected = allTenantIds.every(id => authorizedRegions.includes(id));

  const toggleAll = () => {
    if (isAllSelected) {
      updateUser(user.uid, { extendedRegions: [user.tenantId] });
    } else {
      updateUser(user.uid, { extendedRegions: allTenantIds });
    }
  };

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="px-3 py-1.5 border border-slate-200 rounded-lg text-[10px] font-bold text-slate-700 bg-white flex items-center justify-between w-40"
      >
        <span>{authorizedRegions.length} Region{authorizedRegions.length !== 1 ? 's' : ''} Selected</span>
        <ChevronDown className="w-3 h-3 text-slate-400" />
      </button>
      {isOpen && (
        <div className="absolute z-10 w-60 mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl p-2 space-y-1 max-h-60 overflow-y-auto">
          <label className="flex items-center space-x-2 text-[11px] p-2 rounded-lg cursor-pointer hover:bg-slate-50 border-b border-slate-100 mb-1">
             <input type="checkbox" checked={isAllSelected} onChange={toggleAll} className="rounded border-slate-300 text-[#0094c8] focus:ring-[#0094c8]" />
             <span className="font-black uppercase tracking-wider text-slate-800">Select All</span>
          </label>
          {tenants.map(t => {
            const isChecked = authorizedRegions.includes(t.id);
            return (
              <label key={t.id} className={`flex items-center space-x-2 text-[11px] p-2 rounded-lg cursor-pointer hover:bg-slate-50 ${isChecked ? 'bg-sky-50' : ''}`}>
                <input 
                  type="checkbox" 
                  checked={isChecked} 
                  onChange={() => toggle(user, t.id)} 
                  className="rounded border-slate-300 text-[#0094c8] focus:ring-[#0094c8]"
                />
                <span className={isChecked ? 'text-sky-800 font-bold' : 'text-slate-600'}>{t.name}</span>
              </label>
            )
          })}
        </div>
      )}
    </div>
  );
};

export const AccessTabContent: React.FC<{ 
  users: UserProfile[], 
  tenants: Tenant[],
  updateUser: (uid: string, updates: Partial<UserProfile>) => void
}> = ({ users, tenants, updateUser }) => {
  const [search, setSearch] = useState('');
  
  const excluded = ["Apex Academy WBL portal", "Springfield School District", "Valley Allied Health Coalition"];
  const allowedTenants = tenants.filter(t => !excluded.includes(t.name));

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const toggleRegion = (user: UserProfile, tenantId: string) => {
    const currentRegions = user.extendedRegions || [user.tenantId];
    let nextRegions;
    if (currentRegions.includes(tenantId)) {
      nextRegions = currentRegions.filter(id => id !== tenantId);
    } else {
      nextRegions = [...currentRegions, tenantId];
    }
    updateUser(user.uid, { extendedRegions: nextRegions });
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-lg p-6 space-y-6">
      <div className="flex items-center justify-between border-b border-slate-100 pb-5">
        <h2 className="text-xl font-black text-[#011d3c] tracking-tight uppercase flex items-center space-x-2">
          <Key className="w-6 h-6 text-[#0094c8]" />
          <span>Access Ledger Protocol</span>
        </h2>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 pr-4 py-2 border border-slate-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-[#0094c8] bg-slate-50"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs text-left">
          <thead className="text-slate-400 uppercase tracking-wider font-black text-[9px]">
            <tr>
              <th className="py-3 px-4">User</th>
              <th className="py-3 px-4">Role</th>
              <th className="py-3 px-4">Authorized Regions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredUsers.map(user => {
              const isMultiRegionAllowed = ['superadmin', 'regional_admin', 'organization'].includes(user.role);
              const primaryTenant = tenants.find(t => t.id === user.tenantId);
              const primaryTenantName = primaryTenant ? primaryTenant.name : (user.tenantId === 'global' ? 'Global Portal' : user.tenantId.toUpperCase());
              
              return (
                <tr key={user.uid} className="hover:bg-slate-50 transition-colors">
                  <td className="py-3 px-4">
                    <div className="font-bold text-slate-900">{user.name}</div>
                    <div className="text-[10px] text-slate-500">{user.email}</div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                      user.role === 'superadmin' ? 'bg-amber-100 text-amber-800 border border-amber-200' :
                      user.role === 'regional_admin' ? 'bg-rose-100 text-rose-800 border border-rose-200' :
                      user.role === 'organization' ? 'bg-[#0094c8]/10 text-[#0094c8] border border-[#0094c8]/20' :
                      user.role === 'advisor' ? 'bg-cyan-100 text-cyan-800 border border-cyan-200' :
                      user.role === 'student' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                      'bg-slate-100 text-slate-800'
                    }`}>
                      {user.role === 'regional_admin' ? 'REGIONAL ADMIN' : user.role === 'organization' ? 'EMPLOYER' : user.role.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {isMultiRegionAllowed ? (
                      <RegionDropdown user={user} tenants={allowedTenants} toggle={toggleRegion} updateUser={updateUser} />
                    ) : (
                      <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg text-[10px] font-bold bg-slate-50 border border-slate-200 text-slate-500 font-mono uppercase">
                        <span>{primaryTenantName}</span>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
